import { formatISO } from "date-fns";
import { type FC } from "react";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { ErrorMessageBanner } from "../ErrorMessageBanner";

import type { GradeFormValues } from "./types";

import { AssessmentCategory } from "api/api-types-assessment";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { FormField } from "mds/components/FormField";
import { IconText } from "mds/components/IconText";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { CircleCheckFilledIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectDesignatedSection, selectSectionScorePageCount } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection } from "utils/publishing";

interface GradeFormProps {
  className?: string;
  form: UseFormReturn<GradeFormValues>;
}

const ASSESSMENT_CATEGORIES = [
  AssessmentCategory.prepwork,
  AssessmentCategory.activities,
  AssessmentCategory.assignments,
];

export const GradeForm: FC<GradeFormProps> = ({ className, form }) => {
  const designatedSection = useObjectSelector(selectDesignatedSection, [
    "id",
    "archived_at",
    "kind",
    "section_score_enabled",
    "section_score_published_at",
    "template_id",
    "term_id",
    "title",
  ]);
  const sectionId = designatedSection?.id ?? null;
  const pageCount = useAppSelector((s) => selectSectionScorePageCount(s, sectionId));
  const { isArchived } = useIsArchived();

  const enabled = form.watch("section_score_enabled");

  if (!designatedSection) {
    return null;
  }

  const archived = isArchived(designatedSection);
  const disabled = isLinkedSection(designatedSection) || archived;

  const toggleSectionScoresPublished = () =>
    storeApi.sections.partial_update({
      id: designatedSection.id,
      section_score_published_at: designatedSection.section_score_published_at
        ? null
        : formatISO(new Date()),
    });

  return (
    <FormProvider {...form}>
      <div className={twMerge("flex flex-col", className)}>
        <div className="flex flex-col gap-6">
          <Checkbox
            {...form.register("section_score_enabled", { disabled })}
            eventName="Section Settings - Course score toggle"
            eventProperties={{ sectionTitle: designatedSection.title }}
          >
            {t("settings.section.section_score_enabled")}
          </Checkbox>

          <fieldset className="flex flex-col gap-3">
            <p>{t("settings.section_score_description")}</p>
            {ASSESSMENT_CATEGORIES.map((category) => (
              <FormField
                disabled={!enabled || disabled}
                key={category}
                label={t(`settings.section.section_score_${category}_percentage`)}
                name={`section_score_${category}_percentage`}
                required
              >
                {(controlProps) => (
                  <div className="w-[72px]">
                    <TextField
                      {...form.register(`section_score_${category}_percentage`, {
                        disabled: !enabled || disabled,
                        setValueAs: (value: string): number => {
                          if (value === "") {
                            return 0;
                          }

                          return +value;
                        },
                        validate: (value: number) => {
                          if (!Number.isInteger(value) || value < 0) {
                            return t("settings.section_score_percentage_not_integer");
                          }
                        },
                      })}
                      {...controlProps}
                      className="text-right"
                      iconEnd="%"
                    />
                  </div>
                )}
              </FormField>
            ))}

            <ErrorMessageBanner name="root.section_score_percentages" />
          </fieldset>

          <div className="flex flex-col gap-2 text-black-tint-20">
            {designatedSection.section_score_published_at ? (
              <IconText
                iconStart={<CircleCheckFilledIcon className="text-green" />}
                text={t("settings.section_scores_published")}
              />
            ) : (
              <div>{t("settings.section_scores_unpublished")}</div>
            )}

            <div className="text-sm font-normal">
              {designatedSection.section_score_published_at
                ? t("settings.section_scores_description_published")
                : t("settings.section_scores_description_unpublished")}
            </div>

            <div className="text-sm font-normal">
              {t("settings.section_scores_assessment_count", { count: pageCount })}
            </div>

            <SquareButton
              disabled={!designatedSection.section_score_enabled}
              eventName={
                designatedSection.section_score_published_at
                  ? "Section Settings - Unpublish Course Scores"
                  : "Section Settings - Publish Course Scores"
              }
              kind={designatedSection.section_score_published_at ? "destructive" : "success"}
              size="xs"
              onClick={toggleSectionScoresPublished}
            >
              {designatedSection.section_score_published_at
                ? t("settings.unpublish_section_scores")
                : t("settings.publish_section_scores")}
            </SquareButton>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};
